import type {
  IAuthenticationService,
  ITokenService,
  IUserStorageService,
} from "@/application/ports/services";
import type { ITokenManager } from "@/application/ports/services/ITokenManager";
import type { ILogger } from "@/application/ports/ILogger";
import type { IMonitoring } from "@/application/ports/IMonitoring";
import type { AuthState } from "@/domain/types";
import { UserEntity } from "@/domain/user";
import { getUserErrorMessage } from "@/application/errors/ApiClientError";
import { toSentryContext, clearAuthData } from "@/application/services/authUtils";

export class ValidateAuthStatusUseCase {
  private authService: IAuthenticationService;
  private tokenService: ITokenService;
  private tokenManager: ITokenManager;
  private userStorageService: IUserStorageService;
  private logger: ILogger;
  private monitoring: IMonitoring;

  constructor(
    authService: IAuthenticationService,
    tokenService: ITokenService,
    tokenManager: ITokenManager,
    userStorageService: IUserStorageService,
    logger: ILogger,
    monitoring: IMonitoring,
  ) {
    this.authService = authService;
    this.tokenService = tokenService;
    this.tokenManager = tokenManager;
    this.userStorageService = userStorageService;
    this.logger = logger;
    this.monitoring = monitoring;
  }

  async execute(): Promise<AuthState> {
    try {
      let token = this.tokenService.getToken();

      if (!token) {
        this.clearAuth();
        return {
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        };
      }

      // Expired token: refresh through the TokenManager so the attempt shares
      // the cross-tab lock and in-flight deduplication with regular requests
      if (this.tokenService.isTokenExpired(token)) {
        token = this.tokenService.getRefreshToken() ? await this.tokenManager.forceRefresh() : null;

        if (!token) {
          this.clearAuth();
          return {
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: "Session expired",
          };
        }
      }

      // Validate token with the authentication service
      let validationResult = await this.authService.validateToken(token);

      // If the failure is transient (500, network error), attempt a refresh
      // before giving up — the token may still be valid
      if (
        (!validationResult.isValid || !validationResult.user) &&
        validationResult.errorCode === "transient_error" &&
        this.tokenService.getRefreshToken()
      ) {
        const refreshedToken = await this.tokenManager.forceRefresh();
        if (refreshedToken) {
          validationResult = await this.authService.validateToken(refreshedToken);
        }
      }

      if (!validationResult.isValid || !validationResult.user) {
        this.clearAuth();
        return {
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: validationResult.error || "Invalid authentication",
        };
      }

      // Create user entity for business logic validation
      const userEntity = UserEntity.create(validationResult.user);

      // Check if user can still login (account might have been deactivated)
      if (!userEntity.canLogin()) {
        this.clearAuth();
        return {
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: "Account is no longer active",
        };
      }

      // Update stored user data (in case it changed on the server)
      this.userStorageService.setCurrentUser(validationResult.user);

      // Set Sentry user context after successful validation
      this.monitoring.setUserContext(toSentryContext(validationResult.user, null));

      return {
        user: validationResult.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    } catch (error) {
      this.logger.error("Auth validation failed:", error);
      this.clearAuth();

      return {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: getUserErrorMessage(error, "Authentication check failed"),
      };
    }
  }

  private clearAuth(): void {
    clearAuthData(this.tokenService, this.userStorageService, this.monitoring);
  }
}
